export const LEAD_RETURN_KEY = "after.lead_return";

const OUT_OF_MARKET_RETURN =
  /^\/continue\/pre-planning\/out-of-market(\?[^#\s]*)?$/;

type ReturnStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** A page the lead may be sent back to after signing in: only the
 * out-of-market page, as a same-origin path. */
export function isLeadReturnPath(value: unknown): value is string {
  return typeof value === "string" && OUT_OF_MARKET_RETURN.test(value);
}

function sessionStorageOrNull(): ReturnStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Remembers where to send the lead after they sign in for this
 * submission. Only a resume of the same submission honours it. */
export function storeLeadReturn(
  submissionId: string,
  path: string,
  storage: ReturnStorage | null = sessionStorageOrNull(),
): void {
  if (!storage || !submissionId || !isLeadReturnPath(path)) return;
  try {
    storage.setItem(LEAD_RETURN_KEY, JSON.stringify({ submissionId, path }));
  } catch {
    // Storage may be full or blocked; the lead then lands on the default page.
  }
}

function parseStoredReturn(
  raw: string | null,
): { submissionId: string; path: string } | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const { submissionId, path } = value as Record<string, unknown>;
    if (typeof submissionId !== "string" || !isLeadReturnPath(path)) {
      return null;
    }
    return { submissionId, path };
  } catch {
    return null;
  }
}

/** The stored return path for `submissionId`, cleared as it is read; null
 * when none is stored, it belongs to another submission, or what is stored
 * is not an allowed path. */
export function takeLeadReturn(
  submissionId: string,
  storage: ReturnStorage | null = sessionStorageOrNull(),
): string | null {
  if (!storage) return null;
  try {
    const stored = parseStoredReturn(storage.getItem(LEAD_RETURN_KEY));
    storage.removeItem(LEAD_RETURN_KEY);
    if (!stored) return null;
    return stored.submissionId.toLowerCase() === submissionId.toLowerCase()
      ? stored.path
      : null;
  } catch {
    return null;
  }
}
